import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import ContentLayout from '../components/ContentLayout';

const JsonVsToon: React.FC = () => (
  <>
    <PageSEO
      path="/json-vs-toon"
      title="JSON vs TOON: Format Comparison and Token Savings (2026)"
      description="A side-by-side comparison of JSON and TOON: syntax, token count, readability, and LLM accuracy. See exactly where TOON saves 30-60% of tokens and when to use each format."
      keywords="JSON vs TOON, TOON vs JSON, TOON comparison, token savings, JSON alternative for LLMs"
    />
    <ContentLayout
      related={[
        { to: '/what-is-toon', label: 'What is TOON? A plain-English guide' },
        { to: '/toon-format-spec', label: 'TOON format spec and syntax rules' },
        { to: '/reduce-llm-token-costs', label: 'How to reduce LLM token costs' },
      ]}
    >
      <h1>JSON vs TOON: a side-by-side comparison</h1>
      <p className="content-lead">
        JSON and TOON describe the same data. The difference is how many tokens each spends doing
        it. This page compares the two on syntax, cost, readability, and model accuracy so you can
        decide where each belongs.
      </p>

      <h2>The short version</h2>
      <table className="compare-table">
        <thead>
          <tr>
            <th>&nbsp;</th>
            <th>JSON</th>
            <th>TOON</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Token cost for LLMs</td>
            <td>Baseline</td>
            <td>30-60% lower</td>
          </tr>
          <tr>
            <td>Structure</td>
            <td>Braces, brackets, commas</td>
            <td>Indentation</td>
          </tr>
          <tr>
            <td>Array of objects</td>
            <td>Field names repeated per row</td>
            <td>Field names declared once</td>
          </tr>
          <tr>
            <td>Quotes</td>
            <td>Every key and string</td>
            <td>Only when needed</td>
          </tr>
          <tr>
            <td>Lossless round-trip</td>
            <td>Yes</td>
            <td>Yes</td>
          </tr>
          <tr>
            <td>Best for</td>
            <td>APIs between services</td>
            <td>Data sent into LLM prompts</td>
          </tr>
        </tbody>
      </table>

      <h2>Where the tokens go</h2>
      <p>
        Consider a list of three products. In JSON, the keys <code>id</code>, <code>name</code>, and{' '}
        <code>price</code> appear three times each, every value is comma-separated, and the whole
        thing is wrapped in brackets and braces:
      </p>
      <pre><code>{`{
  "products": [
    { "id": 1, "name": "Pen",    "price": 10 },
    { "id": 2, "name": "Pencil", "price": 5 },
    { "id": 3, "name": "Eraser", "price": 8 }
  ]
}`}</code></pre>
      <p>TOON declares the fields once and lists the rows:</p>
      <pre><code>{`products:
  - id: 1
    name: Pen
    price: 10
  - id: 2
    name: Pencil
    price: 5
  - id: 3
    name: Eraser
    price: 8`}</code></pre>
      <p>
        As the array grows, JSON's per-row overhead grows with it while TOON's header cost stays
        fixed — which is why the savings get larger on bigger datasets.
      </p>

      <h2>Readability and accuracy</h2>
      <p>
        TOON is generally easier for a human to scan because there is less punctuation between you
        and the values. For models, the indentation-based layout is familiar from YAML and outline
        formats in training data, and benchmarks have shown extraction accuracy on par with or
        slightly above JSON — while spending far fewer tokens.
      </p>

      <div className="content-callout">
        <p>
          <strong>Rule of thumb:</strong> keep JSON for communication between your own services and
          databases. Convert to TOON only at the LLM boundary, where every token is billed.
        </p>
      </div>

      <h2>See it on your data</h2>
      <p>
        Paste a real payload into the <Link to="/">converter</Link> and the live counter shows the
        exact token difference for your structure — no guessing from averages.
      </p>
    </ContentLayout>
  </>
);

export default JsonVsToon;
